// Best-effort client-side video compressor (canvas + MediaRecorder re-encode).
// Downscales to ~480p and re-encodes to webm at a reduced bitrate.
// On any failure or non-video input, returns the original file unchanged
// (so audio is never silently dropped).
export async function compressVideo(file, onProgress) {
  if (!file || !file.type || !file.type.startsWith("video/")) return file;
  const supported =
    typeof window !== "undefined" &&
    "MediaRecorder" in window &&
    HTMLCanvasElement.prototype.captureStream;
  if (!supported) return file;

  let url;
  try {
    url = URL.createObjectURL(file);
    const video = document.createElement("video");
    video.src = url;
    video.playsInline = true;
    video.volume = 0; // silent playback, but audio track is still captured
    await new Promise((res, rej) => {
      video.onloadedmetadata = res;
      video.onerror = rej;
    });

    const maxW = 854; // ~480p
    const scale = Math.min(1, maxW / (video.videoWidth || maxW));
    const w = Math.round((video.videoWidth || maxW) * scale);
    const h = Math.round((video.videoHeight || 480) * scale);

    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");

    const stream = canvas.captureStream(25);
    try {
      const src = video.captureStream ? video.captureStream() : video.mozCaptureStream();
      src.getAudioTracks().forEach((t) => stream.addTrack(t));
    } catch (e) {
      if (process.env.NODE_ENV === "development") console.warn("Aucune piste audio disponible pour la compression", e);
    }

    const mime = MediaRecorder.isTypeSupported("video/webm;codecs=vp9,opus")
      ? "video/webm;codecs=vp9,opus"
      : "video/webm";
    const rec = new MediaRecorder(stream, { mimeType: mime, videoBitsPerSecond: 1200000 });
    const chunks = [];
    rec.ondataavailable = (e) => e.data.size && chunks.push(e.data);
    const stopped = new Promise((res) => (rec.onstop = res));
    rec.start(200);

    try {
      await video.play();
    } catch (e) {
      // Autoplay blocked: abort compression to keep the original (with audio).
      rec.stop();
      URL.revokeObjectURL(url);
      return file;
    }

    const duration = video.duration || 0;
    await new Promise((resolve) => {
      const draw = () => {
        if (video.ended || video.paused) return resolve();
        ctx.drawImage(video, 0, 0, w, h);
        if (onProgress && duration) onProgress(Math.min(99, Math.round((video.currentTime / duration) * 100)));
        requestAnimationFrame(draw);
      };
      video.onended = () => resolve();
      draw();
    });

    rec.stop();
    await stopped;
    URL.revokeObjectURL(url);

    const blob = new Blob(chunks, { type: "video/webm" });
    if (!blob.size || blob.size >= file.size) return file; // no gain
    if (onProgress) onProgress(100);
    const name = file.name.replace(/\.[^.]+$/, "") + "-compressed.webm";
    return new File([blob], name, { type: "video/webm" });
  } catch (e) {
    if (url) URL.revokeObjectURL(url);
    return file;
  }
}
